import React from 'react';
import {Box, styled, Stack, SxProps} from '@mui/material';
import {
  Button,
  designSystemToken,
  Icon,
  Headline,
  IconName,
  Tooltip,
} from '@lightricks/react-design-system';
import {scrollbarMixins} from '@contentlab/styles/mixins';

const shouldNotForwardProps = ['isDisabled'];

const Container = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({isDisabled}: {isDisabled: boolean}) => ({
  'boxSizing': 'border-box',
  'backgroundColor': designSystemToken('semantic.bg.primary'),
  'border': `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
  'borderRadius': '32px',
  'padding': '32px',
  'display': 'flex',
  'flexDirection': 'column',
  'justifyContent': 'space-between',
  'height': '100%',
  '*': isDisabled
    ? {
        'pointerEvents': 'none',
        '.config-menu-sections-container': {
          filter: 'blur(0.5px)',
          opacity: 0.95,
        },
      }
    : undefined,
}));

const InnerContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
  height: '100%',
  boxSizing: 'border-box',
}));

const HeaderSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: '8px',
  marginBottom: '12px',
}));

const TitleSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  height: '24px', // Ensure consistent height
  cursor: 'pointer',
}));

const ActionButtons = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

const SectionsContainer = styled(Stack)(() => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  width: '100%',
  margin: '0 -32px',
  padding: '0 32px',
  gap: '24px',
  ...scrollbarMixins.overrideScrollbar(),
}));

interface TopSectionButton {
  label: string;
  icon?: IconName;
  appearance: 'neutral' | 'brand' | 'danger' | 'overlay';
  mode: 'plain' | 'filled' | 'elevated' | 'tinted';
  isDisabled?: boolean;
  isLoading?: boolean;
  tooltipLabel?: string;
  sx?: SxProps;
  onClick: () => void;
}

interface ConfigMenuProps {
  testID?: string;
  title: string;
  topSectionButtons: TopSectionButton[];
  onClose?: () => void;
  isDisabled?: boolean;
  accordionSections: React.ReactNode[];
}

function ConfigMenu({
  testID,
  title,
  topSectionButtons,
  onClose,
  isDisabled = false,
  accordionSections,
}: ConfigMenuProps) {
  return (
    <Container data-testid={testID} isDisabled={isDisabled}>
      <InnerContainer>
        <HeaderSection>
          <TitleSection onClick={onClose}>
            <Box
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                height: '24px', // Match the text height
              }}
              onClick={onClose}
            >
              <Icon
                name="Actions-Arrow-Back-Small"
                size="medium"
                appearance="neutral"
                color={designSystemToken('semantic.fg.secondary')}
              />
            </Box>
            <Headline
              size="sm"
              color={designSystemToken('semantic.fg.primary')}
            >
              {title}
            </Headline>
          </TitleSection>

          <ActionButtons>
            {topSectionButtons.map((button) => (
              <Tooltip
                key={button.label}
                title={button.tooltipLabel}
                arrow
                type="light"
                placement="bottom-end"
                disableHoverListener={!button.isDisabled}
              >
                <span>
                  <Button
                    appearance={button.appearance}
                    mode={button.mode}
                    size="medium"
                    onClick={button.onClick}
                    isLoading={button.isLoading}
                    disabled={button.isDisabled}
                    sx={button.sx}
                    startIcon={
                      button.icon && (
                        <Icon
                          name={button.icon}
                          size="small"
                          appearance={
                            button.isDisabled ? 'disabled' : 'neutral'
                          }
                        />
                      )
                    }
                  >
                    {button.label}
                  </Button>
                </span>
              </Tooltip>
            ))}
          </ActionButtons>
        </HeaderSection>
        <SectionsContainer className="config-menu-sections-container">
          {accordionSections.map((section) => section)}
        </SectionsContainer>
      </InnerContainer>
    </Container>
  );
}

export default ConfigMenu;
